import { useState, useEffect } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { fetchImageMetadata } from '../../../../api/Image';
import { ImageDetails } from '../../../../types/Image';
import { ShoeboxLocation } from '../../../../types/Location';
import { EventData, DateFormat } from '../../../../types/Event';

// Mock collections data (to be replaced with API call)
const mockCollections = [
  { id: '1', name: 'Vacation 2023' },
  { id: '2', name: 'Family Photos' },
  { id: '3', name: 'Work Events' }
];

export const useImageInspector = (imageKey: string | undefined, eventData?: EventData) => {
  // Core data state
  const [metadata, setMetadata] = useState<ImageDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Drawer state
  const [opened, { open, close }] = useDisclosure(true);
  const [ratingDrawerOpened, { open: openRatingDrawer, close: closeRatingDrawer }] = useDisclosure(false);
  const [actionBarHeight, setActionBarHeight] = useState(80); // Default height
  
  // Form editing states
  const [editingDatetime, setEditingDatetime] = useState(false);
  const [editingFileName, setEditingFileName] = useState(false);
  const [editingEvent, setEditingEvent] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  
  // Form field states
  const [fileName, setFileName] = useState('');
  const [eventTitle, setEventTitle] = useState('');
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const [timeValue, setTimeValue] = useState<string | null>(null);
  const [locationOption, setLocationOption] = useState<'auto' | 'manual'>('auto');
  const [locationSearch, setLocationSearch] = useState('');
  const [sublocation, setSublocation] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  
  // Modal states
  const [showAddCollectionModal, setShowAddCollectionModal] = useState(false);
  const [collections, setCollections] = useState(mockCollections);
  
  // Fetch image metadata when component mounts or imageKey changes
  useEffect(() => {
    const getImageMetadata = async () => {
      if (!imageKey) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetchImageMetadata(imageKey);
        console.log('Fetched image metadata:', response);
        
        // The API returns {image: ImageDetails}
        if (response && response.image) {
          // Use the ImageDetails directly
          setMetadata(response.image);
        } else {
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        console.error('Error fetching image metadata:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch image metadata');
        // We can't use mock data as it doesn't match ImageDetails
        setMetadata(null);
      } finally {
        setLoading(false);
      }
    };
    
    getImageMetadata();
  }, [imageKey]);
  
  // Update form state when metadata is loaded
  useEffect(() => {
    if (metadata) {
      setFileName(metadata.display_file_name || metadata.file?.display_name || '');
      
      // If we have event data, use that instead of metadata.event_key
      if (!eventData) {
        setEventTitle(metadata.event_key || '');
      }
      
      // Parse date from metadata if available and if we don't have event data
      if (metadata.datetime && !eventData) {
        try {
          setDateValue(new Date(metadata.datetime));
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      }
      
      // If we have event data, use its date
      if (eventData && eventData.date) {
        setDateValue(new Date(eventData.date));
      }
      
      setTimeValue(null);
      
      // Set location fields if available and if we don't have event data with location
      if (metadata.location && !(eventData && eventData.location)) {
        setSublocation(metadata.location?.sublocation || '');
        setCity(metadata.location?.city || '');
        setState(metadata.location?.state || '');
        setCountry(metadata.location?.country || '');
      }
      
      // If we have event data with location, use that instead
      if (eventData) {
        // Check for location_description first (new field)
        if (eventData.location_description) {
          setSublocation(eventData.location_description);
        }
        // Then check for location which could be string (old format) or ShoeboxLocation (new format)
        else if (eventData.location) {
          if (typeof eventData.location === 'string') {
            // Legacy format: location is a string
            setSublocation(eventData.location);
          } else {
            // New format: location is a ShoeboxLocation object
            // Use address or sublocation as the sublocation
            setSublocation(eventData.location.sublocation || eventData.location.address || '');
            
            // Use city, state, country from the location object if available
            setCity(eventData.location.city || '');
            setState(eventData.location.state || '');
            setCountry(eventData.location.country || '');
            
            // Skip the metadata location since we have better data from eventData
            return;
          }
        }
        
        // If we don't have complete location data from eventData, use metadata
        if (metadata.location) {
          setCity(metadata.location.city || '');
          setState(metadata.location.state || '');
          setCountry(metadata.location.country || '');
        }
      }
      
      // Set keywords if available (empty array as fallback) and if we don't have event data with keywords
      if (!eventData || !eventData.keywords) {
        setKeywords(metadata.keywords || []);
      }
      
      // If we have event data with keywords, use those instead
      if (eventData && eventData.keywords) {
        setKeywords(eventData.keywords);
      }
    }
  }, [metadata, eventData]);
  
  // Effect to measure and set the action bar height
  useEffect(() => {
    const updateActionBarHeight = () => {
      const actionBar = document.getElementById('actionBar');
      if (actionBar) {
        const height = actionBar.offsetHeight;
        document.documentElement.style.setProperty('--action-bar-height', `${height}px`);
        setActionBarHeight(height);
      }
    };
    
    // Set up resize observer to update when size changes
    const actionBar = document.getElementById('actionBar');
    if (actionBar) {
      const resizeObserver = new ResizeObserver(updateActionBarHeight);
      resizeObserver.observe(actionBar);
      return () => resizeObserver.disconnect();
    }
  }, [opened]); // Re-run when drawer state changes
  
  // API function to save metadata changes
  const saveMetadata = async (updates: Partial<ImageDetails>) => {
    if (!metadata || !imageKey) return;
    
    console.log(`PUT /image?id=${imageKey}`, {
      updates
    });
    
    // Update local state with the changes
    setMetadata(currentMetadata => {
      if (!currentMetadata) return null;
      return {
        ...currentMetadata,
        ...updates
      };
    });
    
    // In a real implementation, this would be an actual API call
    showNotification({
      title: 'Changes saved',
      message: 'Your changes have been saved successfully.',
      color: 'green'
    });
  };
  
  // Function to save file name
  const saveFileName = () => {
    if (!metadata) return;
    
    saveMetadata({
      display_file_name: fileName
    });
    setEditingFileName(false);
  };
  
  // Function to save event details
  const saveEventDetails = () => {
    if (!metadata) return;
    
    // Update event key and datetime
    saveMetadata({
      event_key: eventTitle,
      datetime: dateValue?.toISOString() || metadata.datetime
    });
    setEditingEvent(false);
    
    // If we have event data and a callback to update it, call that too
    if (eventData && eventData.key) {
      console.log('Event data should be updated in parent component');
      // The parent component should handle updating the event data
    }
  };
  
  // Function to save location details
  const saveLocationDetails = () => {
    if (!metadata) return;
    
    // Create a proper Location object according to the ShoeboxLocation interface
    // Using type assertion to ensure it matches the ShoeboxLocation interface from ImageDetails
    const locationUpdate: ShoeboxLocation = {
      sublocation,
      city,
      state,
      country,
      // Only include geolocation if it exists in the original location
      ...(metadata.location?.geolocation ? { geolocation: metadata.location?.geolocation } : {})
    };
    
    // Update only the location field
    // Using type assertion to match the expected type in ImageDetails
    saveMetadata({
      location: locationUpdate as any // Use 'any' to bypass type checking temporarily
    });
    setEditingLocation(false);
  };
  
  // Function to add a keyword
  const addKeyword = (keyword: string) => {
    if (!keyword.trim()) return;
    
    const updatedKeywords = [...keywords, keyword.trim()];
    setKeywords(updatedKeywords);
    setNewKeyword('');
    
    // Save keywords to metadata
    if (metadata) {
      saveMetadata({
        // Update keywords field
      });
    }
  };
  
  // Function to remove a keyword
  const removeKeyword = (index: number) => {
    const updatedKeywords = [...keywords];
    updatedKeywords.splice(index, 1);
    setKeywords(updatedKeywords);
    
    // Save updated keywords to metadata
    if (metadata) {
      saveMetadata({
        // Update keywords field
      });
    }
  };
  
  // Function to add to collection
  const addCollection = (collectionName: string) => {
    console.log(`Adding image to collection: ${collectionName}`);
    setShowAddCollectionModal(false);
    
    showNotification({
      title: 'Added to collection',
      message: `Image added to ${collectionName}`,
      color: 'green'
    });
  };
  
  return {
    // Core data
    metadata,
    loading,
    error,
    
    // Drawer state
    drawerState: {
      opened,
      open,
      close,
      ratingDrawerOpened,
      openRatingDrawer,
      closeRatingDrawer,
      actionBarHeight,
      setActionBarHeight,
      editingDatetime,
      setEditingDatetime,
      editingFileName,
      setEditingFileName,
      editingEvent,
      setEditingEvent,
      editingLocation,
      setEditingLocation
    },
    
    // Form state
    formState: {
      fileName,
      setFileName,
      eventTitle,
      setEventTitle,
      dateValue,
      setDateValue,
      timeValue,
      setTimeValue,
      locationOption,
      setLocationOption,
      locationSearch,
      setLocationSearch,
      sublocation,
      setSublocation,
      city,
      setCity,
      state,
      setState,
      country,
      setCountry,
      newKeyword,
      setNewKeyword,
      keywords,
      setKeywords
    },
    
    // Modals state
    modalsState: {
      showAddCollectionModal,
      setShowAddCollectionModal,
      collections
    },
    
    // Actions
    saveFileName,
    saveEventDetails,
    saveLocationDetails,
    addKeyword,
    removeKeyword,
    handleAddToCollection: addCollection,
    handleCloseCollectionModal: () => setShowAddCollectionModal(false)
  };
};
